// §4 S3 — WO create/release modal (Module 04, slice 04-A). Launched from S1
// (create → release) and from S2 (the Release transition opens it at step 2, so
// the ATP panel travels with the action).
//
// STEP 2'S TWO-PHASE RELEASE IS THE POINT (§4 S3 / §5 row 4): the release is
// POSTED WITHOUT `acknowledge_shortages` first — a planner must not be able to
// release short by accident. Only the server's 409
// `WO_RELEASE_SHORTAGES_UNACKNOWLEDGED` opens the release-anyway confirmation,
// and only the confirmation reposts `{acknowledge_shortages: true}`. The 200's
// `shortages[]` is the AUTHORITATIVE record; the client ATP panel is advisory
// and says so.
//
// "Earliest PO due from Module 10" (§4 S3 as written) is unbuildable — module
// 10 does not exist — so the amber row shows the shortfall only, with a
// degraded note naming procurement (spec §4 S3 amended in this change, D-071).
//
// PRE-PEGGED (D-290). A sales order line's "Plan a work order" opens this modal
// through the list's `peg_order_id` / `peg_line_id` params, and the work order
// is then made FOR that line: a banner names the customer order and the line
// (read from `GET /v1/orders/{id}`, module 09's own detail), the product is the
// line's and cannot be changed — the server refuses any other with 422
// ORDER_LINE_PRODUCT_MISMATCH — the quantity starts at what the line still has
// to ship and the unit at the line's own, and the create body carries
// `sales_order_line_id`. Both steps can meet module 09's peg refusals (the
// create's four, and the release's order-facility 403, CREDIT_BLOCKED and
// SHIPMENT_OPEN on top), and each one reads as its own sentence
// (`createReleaseErrorMessage`: `production.problem`, plus the modal's own
// 403 sentences), never as the server's English title. The facility stays the
// planner's choice: the order
// ships from one plant and may be made at another. A pegged read is mounted
// only while pegged (`PeggedCreateReleaseModal`), so an ordinary create never
// asks module 09 anything.

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Modal } from "../../../../components/modal";
import { useTerm } from "../../../onboarding/hooks/terminology";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import { RequestError } from "../../../../lib/request";
import {
  useComponentAtp,
  useCreateWorkOrder,
  useDepartments,
  useEffectiveBom,
  useEffectiveRouting,
  useFacilities,
  usePegOrder,
  useProductBoms,
  useProductIndex,
  useProductRoutings,
  useProductSearch,
  useReleaseWorkOrder,
  useUoms,
  useWorkOrder,
  type PegOrderLine,
  type WorkOrder,
  type WorkOrderCreateRequest,
  type WorkOrderShortage,
} from "../hooks/production-queries";
import { useProductionPermissions } from "../hooks/permissions";
import { createReleaseErrorMessage, formatQty, isValidQtyInput, parseQty } from "../lib/format";
import { unitSymbol } from "../../../../lib/quantity";

interface PickedProduct {
  readonly id: string;
  readonly sku: string;
  readonly name: string;
  readonly base_uom_id: string;
}

/** The sales order line a create is pegged to, as the list's URL carries it. */
export interface PegRequest {
  readonly orderId: string;
  readonly lineId: string;
}

/**
 * The peg as the create step reads it. `ready` only once the customer order
 * answered AND carries the line; `missing` is an order that answered without
 * it (a stale link), which the server would refuse 404 ORDER_LINE_NOT_FOUND —
 * said before the press instead of after it.
 */
type PegContext =
  | {
      readonly state: "loading" | "error" | "missing";
      readonly orderId: string;
    }
  | {
      readonly state: "ready";
      readonly orderId: string;
      readonly orderNo: string;
      readonly line: PegOrderLine;
      /** The line's product — its id always, its SKU once the catalogue
       * answers (the create never waits on that read). */
      readonly product: PickedProduct;
    };

/** The scale of `parseQty` (×1e6), and one step of the 3 dp the wire carries (D-041). */
const QTY_SCALE = 1_000_000n;
const QTY_3DP_STEP = 1_000n;

/**
 * What an order line still has to ship — `qty_ordered − qty_shipped` — as the
 * canonical decimal text the quantity field takes, on the scaled integers
 * (never floats). "" when nothing is left or either side is unreadable: an
 * empty field the planner fills beats a prefilled zero the server refuses.
 */
export function openQtyText(ordered: string, shipped: string): string {
  const orderedScaled = parseQty(ordered);
  const shippedScaled = parseQty(shipped);
  if (orderedScaled === undefined || shippedScaled === undefined) return "";
  const open = orderedScaled - shippedScaled;
  if (open <= 0n) return "";
  const whole = open / QTY_SCALE;
  const frac = (open % QTY_SCALE) / QTY_3DP_STEP;
  const fracText = frac.toString().padStart(3, "0").replace(/0+$/u, "");
  return fracText.length > 0 ? `${whole.toString()}.${fracText}` : whole.toString();
}

/** The refusal's problem code, for the `data-code` a walkthrough reads. */
function problemCodeOf(error: unknown): string | undefined {
  return error instanceof RequestError ? error.code : undefined;
}

/** Module 01's own 404 for "nothing is effective here today" — the same refusal
 * `POST /v1/work-orders` raises when it resolves the version itself. */
function isNoEffectiveVersion(error: unknown): boolean {
  return (
    error instanceof RequestError &&
    (error.code === "NO_EFFECTIVE_BOM" || error.code === "NO_EFFECTIVE_ROUTING")
  );
}

export function CreateReleaseModal({
  onClose,
  releaseWorkOrder,
  peg,
}: {
  onClose: () => void;
  /** Open directly at the release step for an existing draft (S2's Release). */
  releaseWorkOrder?: WorkOrder | undefined;
  /** Make the work order FOR this sales order line (D-290). */
  peg?: PegRequest | undefined;
}) {
  return peg === undefined ? (
    <CreateReleaseBody onClose={onClose} releaseWorkOrder={releaseWorkOrder} peg={undefined} />
  ) : (
    <PeggedCreateReleaseModal onClose={onClose} peg={peg} />
  );
}

/** The pegged variant's reads — the customer order and the line's product —
 * mounted only while pegged. */
function PeggedCreateReleaseModal({ onClose, peg }: { onClose: () => void; peg: PegRequest }) {
  const order = usePegOrder(peg.orderId);
  const line = order.data?.lines.find((candidate) => candidate.id === peg.lineId);
  const products = useProductIndex(line === undefined ? [] : [line.product_id]);
  let context: PegContext;
  if (order.isPending) {
    context = { state: "loading", orderId: peg.orderId };
  } else if (order.data === undefined) {
    context = { state: "error", orderId: peg.orderId };
  } else if (line === undefined) {
    context = { state: "missing", orderId: peg.orderId };
  } else {
    const known = products.byId.get(line.product_id);
    context = {
      state: "ready",
      orderId: peg.orderId,
      orderNo: order.data.order_no,
      line,
      product: known ?? {
        id: line.product_id,
        sku: "",
        name: line.description,
        base_uom_id: "",
      },
    };
  }
  return <CreateReleaseBody onClose={onClose} releaseWorkOrder={undefined} peg={context} />;
}

function CreateReleaseBody({
  onClose,
  releaseWorkOrder,
  peg,
}: {
  onClose: () => void;
  releaseWorkOrder: WorkOrder | undefined;
  peg: PegContext | undefined;
}) {
  const { t } = useTranslation();
  const [created, setCreated] = useState<WorkOrder | undefined>(undefined);
  const workOrder = created ?? releaseWorkOrder;
  const perms = useProductionPermissions();
  const canRelease = perms.has("production.wo.release");

  const [step, setStep] = useState<"create" | "release">(
    releaseWorkOrder !== undefined ? "release" : "create",
  );

  return (
    // THE OVERLAY PRIMITIVE (F5): Escape, a backdrop click and a labelled ✕
    // all close this panel, focus is trapped inside it and restored to the
    // control that opened it. This dialog had none of those — its only exits
    // were two differently-worded step buttons.
    <Modal
      label={
        step === "create"
          ? t("production.createModal.stepCreate")
          : t("production.createModal.stepRelease")
      }
      closeLabel={t("production.common.close")}
      onClose={onClose}
      className="production-modal"
    >
      <div className="modal-steps">
        <span className={step === "create" ? "is-current" : undefined}>
          {t("production.createModal.stepCreate")}
        </span>
        <span className={step === "release" ? "is-current" : undefined}>
          {t("production.createModal.stepRelease")}
        </span>
      </div>
      {/* Above BOTH steps: the release is where the credit hold and an open
          shipment refuse, and the way back to the order is wanted most after
          the release succeeded. */}
      {peg !== undefined ? <PegBanner peg={peg} /> : null}
      {step === "create" || workOrder === undefined ? (
        <CreateStep
          peg={peg}
          onCancel={onClose}
          onCreated={(wo) => {
            setCreated(wo);
            if (canRelease) {
              setStep("release");
            } else {
              onClose();
            }
          }}
        />
      ) : (
        <ReleaseStep workOrder={workOrder} onClose={onClose} />
      )}
    </Modal>
  );
}

/** Which customer order and line the work order is for — or why that is not
 * known yet. One node, `data-state` for the walkthrough. */
function PegBanner({ peg }: { peg: PegContext }) {
  const { t } = useTranslation();
  let body: ReactNode;
  if (peg.state === "ready") {
    body = (
      <>
        {t("production.createModal.pegBanner", { order: peg.orderNo, line: peg.line.line_no })}{" "}
        <Link
          to="/sales/orders/$orderId"
          params={{ orderId: peg.orderId }}
          data-testid="wo-peg-order-link"
        >
          {t("production.createModal.pegBackToOrder")}
        </Link>
      </>
    );
  } else if (peg.state === "loading") {
    body = t("production.createModal.pegLoading");
  } else if (peg.state === "error") {
    body = t("production.createModal.pegError");
  } else {
    body = t("production.problem.ORDER_LINE_NOT_FOUND");
  }
  return (
    <p className="production-note" role="note" data-testid="wo-peg-banner" data-state={peg.state}>
      {body}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — create (§5 row 1; gate production.wo.write sits on the openers)
// ---------------------------------------------------------------------------

function CreateStep({
  peg,
  onCreated,
  onCancel,
}: {
  peg: PegContext | undefined;
  onCreated: (wo: WorkOrder) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const { term } = useTerm();
  const create = useCreateWorkOrder();

  const [facilityId, setFacilityId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [picked, setProduct] = useState<PickedProduct | undefined>(undefined);
  // THE PEG LOCKS THE PRODUCT: a pegged create makes the line's product or
  // nothing (the picker is not rendered), and until the order answers there is
  // no product, so Create stays disabled rather than posting an unpegged draft.
  const pegReady = peg?.state === "ready" ? peg : undefined;
  const product = peg === undefined ? picked : pegReady?.product;
  const [bomChoice, setBomChoice] = useState("");
  const [routingChoice, setRoutingChoice] = useState("");
  const [qty, setQty] = useState("");
  const [uomCode, setUomCode] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState("3");
  const [notes, setNotes] = useState("");
  const [validationKey, setValidationKey] = useState<string | undefined>(undefined);

  const facilities = useFacilities();
  const departments = useDepartments(facilityId);
  const debouncedQuery = useDebouncedValue(productQuery, 250);
  const search = useProductSearch(debouncedQuery.trim());
  const uoms = useUoms();

  const productId = product?.id ?? "";
  const effectiveBom = useEffectiveBom(productId, facilityId);
  const effectiveRouting = useEffectiveRouting(productId, facilityId);
  const boms = useProductBoms(productId);
  const routings = useProductRoutings(productId);

  // Default the UoM code from the product's base UoM once both are known.
  const uomCodeById = useMemo(() => {
    const map = new Map<string, string>();
    for (const uom of uoms.data?.items ?? []) map.set(uom.id, uom.code);
    return map;
  }, [uoms.data]);
  useEffect(() => {
    // A pegged create takes the LINE's unit instead (below).
    if (peg !== undefined || product === undefined || uomCode.length > 0) return;
    const code = uomCodeById.get(product.base_uom_id);
    if (code !== undefined) setUomCode(code);
  }, [peg, product, uomCode, uomCodeById]);

  // Pegged: the quantity starts at what the line still has to ship — ONCE, so
  // the planner's own edit is never overwritten by a re-render — and the unit
  // at the line's own (filled whenever it is empty, as the product default is).
  const [qtySeeded, setQtySeeded] = useState(false);
  useEffect(() => {
    if (pegReady === undefined || qtySeeded) return;
    setQty(openQtyText(pegReady.line.qty_ordered, pegReady.line.qty_shipped));
    setQtySeeded(true);
  }, [pegReady, qtySeeded]);
  useEffect(() => {
    if (pegReady === undefined || uomCode.length > 0) return;
    const code = uomCodeById.get(pegReady.line.uom_id);
    if (code !== undefined) setUomCode(code);
  }, [pegReady, uomCode, uomCodeById]);

  const bomOverridden = bomChoice.length > 0 && bomChoice !== effectiveBom.data?.id;
  const routingOverridden = routingChoice.length > 0 && routingChoice !== effectiveRouting.data?.id;

  // A FORM MAY NOT SUBMIT INTO A REFUSAL IT ALREADY HOLDS (surface review F8).
  //
  // `POST /v1/work-orders` pins a BOM and a routing: with no explicit
  // `bom_id`/`routing_id` it resolves the effective version and answers 404
  // `NO_EFFECTIVE_BOM` / `NO_EFFECTIVE_ROUTING` when there is none for this
  // product at this facility today (work-orders.service.ts `pinBom`/
  // `pinRouting`). This panel ASKS THE SAME QUESTION on the way in — the
  // versions section reads `…/boms/effective` — so when that read has already
  // come back 404, pressing Create was a guaranteed round trip to the
  // identical refusal, phrased as a red banner after the click instead of a
  // stated blocker before it. Picking an explicit version un-blocks it,
  // because then the server pins what was chosen and never resolves.
  const bomMissing = bomChoice.length === 0 && isNoEffectiveVersion(effectiveBom.error);
  const routingMissing = routingChoice.length === 0 && isNoEffectiveVersion(effectiveRouting.error);
  const versionBlocker = bomMissing
    ? t("production.createModal.noEffectiveBom")
    : routingMissing
      ? t("production.createModal.noEffectiveRouting")
      : undefined;

  const submit = () => {
    if (product === undefined || facilityId.length === 0) return;
    if (!isValidQtyInput(qty, { positive: true })) {
      setValidationKey("production.createModal.qtyInvalid");
      return;
    }
    setValidationKey(undefined);
    const body: WorkOrderCreateRequest = {
      product_id: product.id,
      qty_planned: qty.trim(),
      uom: uomCode,
      facility_id: facilityId,
      priority: Number(priority),
    };
    if (departmentId.length > 0) body.department_id = departmentId;
    if (bomChoice.length > 0) body.bom_id = bomChoice;
    if (routingChoice.length > 0) body.routing_id = routingChoice;
    if (dueDate.length > 0) body.due_date = dueDate;
    if (notes.trim().length > 0) body.notes = notes.trim();
    if (pegReady !== undefined) body.sales_order_line_id = pegReady.line.id;
    create.mutate(body, {
      onSuccess: (res) => {
        onCreated(res.work_order);
      },
    });
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <label className="filter-chip">
        <span className="filter-chip-label">{term("facility")}</span>
        <select
          value={facilityId}
          required
          onChange={(event) => {
            setFacilityId(event.target.value);
            setDepartmentId("");
            setBomChoice("");
            setRoutingChoice("");
          }}
        >
          {/* "Any" IS A FILTER'S WORD, and this is a REQUIRED field on a
              create form (F14 of the list screen, F7 here): a work order is
              made AT a plant — there is no "any" — and the same select was
              telling the planner they could leave it unset while `required`
              and the disabled submit said the opposite. */}
          <option value="">{t("production.createModal.pickFacility")}</option>
          {(facilities.data?.items ?? []).map((facility) => (
            <option key={facility.id} value={facility.id}>
              {facility.name}
            </option>
          ))}
        </select>
      </label>

      <label className="filter-chip">
        <span className="filter-chip-label">
          {term("department")} ({t("production.createModal.departmentOptional")})
        </span>
        <select
          value={departmentId}
          disabled={facilityId.length === 0}
          onChange={(event) => {
            setDepartmentId(event.target.value);
          }}
        >
          <option value="">{t("production.createModal.departmentOptional")}</option>
          {(departments.data?.items ?? []).map((department) => (
            <option key={department.id} value={department.id}>
              {department.name}
            </option>
          ))}
        </select>
      </label>

      {pegReady !== undefined ? (
        // Locked to the line: shown in the picker's place, never searched for.
        // Until the order answers there is nothing to show — the banner says
        // why — and Create stays disabled.
        <div
          className="filter-chip"
          data-testid="wo-peg-product"
          data-product-id={pegReady.product.id}
        >
          <span className="filter-chip-label">{term("product")}</span>
          <span>
            {pegReady.product.sku.length > 0
              ? `${pegReady.product.sku} · ${pegReady.product.name}`
              : pegReady.product.name}
          </span>
          <span className="production-hint">{t("production.createModal.pegProductLocked")}</span>
        </div>
      ) : null}
      {peg !== undefined ? null : (
        <label className="filter-chip">
          <span className="filter-chip-label">{t("production.createModal.searchLabel")}</span>
          <input
            type="search"
            value={productQuery}
            placeholder={t("production.createModal.searchPlaceholder")}
            onChange={(event) => {
              setProductQuery(event.target.value);
              setProduct(undefined);
              setBomChoice("");
              setRoutingChoice("");
              setUomCode("");
            }}
          />
        </label>
      )}
      {peg === undefined && product === undefined && debouncedQuery.trim().length > 0 ? (
        (search.data?.items ?? []).length === 0 && !search.isPending ? (
          <p className="production-hint">{t("production.createModal.noProducts")}</p>
        ) : (
          <ul className="picker-results">
            {(search.data?.items ?? []).slice(0, 10).map((item) => (
              <li key={item.id}>
                <Button
                  onClick={() => {
                    setProduct({
                      id: item.id,
                      sku: item.sku,
                      name: item.name,
                      base_uom_id: item.base_uom_id,
                    });
                    setProductQuery(`${item.sku} · ${item.name}`);
                  }}
                >
                  {item.sku} · {item.name}
                </Button>
              </li>
            ))}
          </ul>
        )
      ) : null}

      {product !== undefined ? (
        facilityId.length === 0 ? (
          <p className="production-note">{t("production.createModal.pickFacilityFirst")}</p>
        ) : (
          <section className="production-versions">
            <h3>{t("production.createModal.versionsTitle")}</h3>
            {effectiveBom.error instanceof RequestError ? (
              <p className="production-note" role="alert">
                {createReleaseErrorMessage(t, effectiveBom.error)}
              </p>
            ) : null}
            {effectiveRouting.error instanceof RequestError ? (
              <p className="production-note" role="alert">
                {createReleaseErrorMessage(t, effectiveRouting.error)}
              </p>
            ) : null}
            <label className="filter-chip">
              <span className="filter-chip-label">
                {term("bom")} — {t("production.createModal.overrideVersion")}
              </span>
              <select
                value={bomChoice}
                onChange={(event) => {
                  setBomChoice(event.target.value);
                }}
              >
                <option value="">
                  {t("production.createModal.activeVersion")}
                  {effectiveBom.data !== undefined
                    ? ` — ${t("production.createModal.versionOption", { version: effectiveBom.data.version })}`
                    : ""}
                </option>
                {(boms.data?.items ?? [])
                  .filter((bom) => bom.status !== "draft")
                  .map((bom) => (
                    <option key={bom.id} value={bom.id}>
                      {t("production.createModal.versionOption", { version: bom.version })}
                    </option>
                  ))}
              </select>
            </label>
            <label className="filter-chip">
              <span className="filter-chip-label">
                {t("production.createModal.routing")} —{" "}
                {t("production.createModal.overrideVersion")}
              </span>
              <select
                value={routingChoice}
                onChange={(event) => {
                  setRoutingChoice(event.target.value);
                }}
              >
                <option value="">
                  {t("production.createModal.activeVersion")}
                  {effectiveRouting.data !== undefined
                    ? ` — ${t("production.createModal.versionOption", { version: effectiveRouting.data.version })}`
                    : ""}
                </option>
                {(routings.data?.items ?? [])
                  .filter((routing) => routing.status !== "draft")
                  .map((routing) => (
                    <option key={routing.id} value={routing.id}>
                      {t("production.createModal.versionOption", { version: routing.version })}
                    </option>
                  ))}
              </select>
            </label>
            {bomOverridden || routingOverridden ? (
              <p className="production-note" role="note" data-testid="version-warning">
                {t("production.createModal.versionWarning")}
              </p>
            ) : null}
          </section>
        )
      ) : null}

      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.createModal.qty")}</span>
        <input
          value={qty}
          inputMode="decimal"
          required
          onChange={(event) => {
            setQty(event.target.value);
          }}
        />
      </label>
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.createModal.uom")}</span>
        <select
          value={uomCode}
          required
          onChange={(event) => {
            setUomCode(event.target.value);
          }}
        >
          <option value="">{t("production.createModal.uom")}</option>
          {(uoms.data?.items ?? []).map((uom) => (
            <option key={uom.id} value={uom.code}>
              {uom.code} — {uom.name}
            </option>
          ))}
        </select>
      </label>
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.createModal.dueDate")}</span>
        <input
          type="date"
          value={dueDate}
          onChange={(event) => {
            setDueDate(event.target.value);
          }}
        />
      </label>
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.createModal.priority")}</span>
        <select
          value={priority}
          onChange={(event) => {
            setPriority(event.target.value);
          }}
        >
          {["1", "2", "3", "4", "5"].map((value) => (
            <option key={value} value={value}>
              {t("production.common.priorityLabel", { value })}
            </option>
          ))}
        </select>
      </label>
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.createModal.notes")}</span>
        <textarea
          value={notes}
          onChange={(event) => {
            setNotes(event.target.value);
          }}
        />
      </label>

      {validationKey !== undefined ? (
        <p className="form-error" role="alert">
          {t(validationKey)}
        </p>
      ) : null}
      {create.isError ? (
        <p
          className="form-error"
          role="alert"
          data-testid="wo-create-failed"
          data-code={problemCodeOf(create.error)}
        >
          {createReleaseErrorMessage(t, create.error, "production.list.error")}
        </p>
      ) : null}

      {versionBlocker !== undefined ? (
        <p className="production-note" role="note" data-testid="create-blocked">
          {versionBlocker}
        </p>
      ) : null}

      <div className="form-actions">
        {/* type="submit" explicitly — Button defaults to type="button" and this
            control submits the create <form>. */}
        <Button
          type="submit"
          variant="primary"
          busy={create.isPending}
          busyLabel={t("production.common.saving")}
          disabled={
            product === undefined || facilityId.length === 0 || versionBlocker !== undefined
          }
        >
          {t("production.createModal.submitCreate")}
        </Button>
        <Button onClick={onCancel}>{t("production.common.cancel")}</Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — release (§5 row 4; gate production.wo.release sits on the openers)
// ---------------------------------------------------------------------------

function ReleaseStep({ workOrder, onClose }: { workOrder: WorkOrder; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const release = useReleaseWorkOrder();

  // ONE ORDER, ONE REQUIRED QUANTITY (D-041; spec 01 §2.7 / 04 §3.5).
  //
  // This panel used to re-derive the requirement in the browser as
  // `qty_per × qty_planned ÷ output_qty` off the BOM. That is a SECOND
  // implementation of the material plan, and it disagreed with the server's in
  // two independent ways: it dropped the BOM line's component `scrap_pct`
  // entirely (the server divides by yield — `backflushRequiredQty`), and it did
  // the arithmetic in IEEE-754 where the server uses scaled integers with one
  // half-even rounding. On the seeded КГ 3×70 (copper 1.86 kg/m at 3 %) a
  // 500 m order needs 958.763 kg; the old panel said 930.000 — so it could
  // report a component COVERED that the release then reported SHORT, and the
  // Materials tab of the same order quoted a third-of-a-tonne-different number
  // beside it.
  //
  // The server already publishes exactly one answer: `material_lines[]` on the
  // work-order detail, built by `materialLineViews` with NO status branch — a
  // draft carries it, which is the state this step always opens in — and
  // `shortages[].required` on the release response quotes the same field. So
  // the panel reads it rather than recomputing it.
  const detail = useWorkOrder(workOrder.id);
  const planLines = useMemo(() => detail.data?.material_lines ?? [], [detail.data]);
  const products = useProductIndex(planLines.map((line) => line.component_product_id));

  const [shortComponents, setShortComponents] = useState<ReadonlySet<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [failure, setFailure] = useState<unknown>(undefined);
  const [result, setResult] = useState<
    { workOrder: WorkOrder; shortages: readonly WorkOrderShortage[] } | undefined
  >(undefined);

  const markShort = (componentId: string, short: boolean) => {
    setShortComponents((prev) => {
      const has = prev.has(componentId);
      if (has === short) return prev;
      const next = new Set(prev);
      if (short) next.add(componentId);
      else next.delete(componentId);
      return next;
    });
  };

  const post = (acknowledge: boolean) => {
    setFailure(undefined);
    release.mutate(
      acknowledge ? { id: workOrder.id, acknowledgeShortages: true } : { id: workOrder.id },
      {
        onSuccess: (res) => {
          setConfirming(false);
          setResult({ workOrder: res.work_order, shortages: res.shortages });
        },
        onError: (error) => {
          if (
            error instanceof RequestError &&
            error.code === "WO_RELEASE_SHORTAGES_UNACKNOWLEDGED"
          ) {
            // The server said "short and unacknowledged" — THAT, not the
            // advisory panel, is what opens the release-anyway confirmation.
            setConfirming(true);
            return;
          }
          setFailure(error);
        },
      },
    );
  };

  const shortageColumns: DataTableColumn<WorkOrderShortage>[] = [
    {
      key: "component_product_id",
      header: t("production.createModal.atpTitle"),
      id: true,
      render: (shortage) =>
        products.byId.get(shortage.component_product_id)?.name ?? shortage.component_product_id,
    },
    {
      // D-221: the three quantities are in the COMPONENT'S base unit, which is
      // not necessarily the unit the BOM line beside them is authored in — a
      // cable BOM states copper in metres and the yard holds it in kilograms.
      // Printing them unlabelled is what hid a metre-versus-kilogram comparison
      // in the server for two months.
      key: "uom",
      header: t("production.createModal.uom"),
      render: (shortage) => shortage.uom,
    },
    {
      key: "required",
      header: t("production.createModal.colRequired"),
      numeric: true,
      render: (shortage) => formatQty(shortage.required, { locale: i18n.language }),
    },
    {
      key: "available",
      header: t("production.createModal.colAvailable"),
      numeric: true,
      render: (shortage) => formatQty(shortage.available, { locale: i18n.language }),
    },
    {
      key: "shortfall",
      header: t("production.createModal.colShortfall"),
      numeric: true,
      render: (shortage) => formatQty(shortage.shortfall, { locale: i18n.language }),
    },
  ];

  if (result !== undefined) {
    return (
      <section>
        <p className="inv-success" role="status">
          {t("production.createModal.released")} {result.workOrder.wo_no}
        </p>
        {result.shortages.length > 0 ? (
          <>
            <h3>{t("production.createModal.shortagesTitle")}</h3>
            <DataTable
              columns={shortageColumns}
              rows={result.shortages}
              rowKey={(shortage) => shortage.component_product_id}
              empty={null}
            />
          </>
        ) : null}
        <div className="form-actions">
          <Button variant="primary" onClick={onClose}>
            {t("production.createModal.done")}
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="wo-release-step">
      <h3>{t("production.createModal.atpTitle")}</h3>
      <p className="production-hint">{t("production.createModal.atpAdvisory")}</p>
      {/* The plan is a fetch now, so it has the two states a fetch has. It
       * previously had neither: while the BOM loaded the panel rendered ZERO
       * rows, which reads as "nothing is required" — the most dangerous
       * possible default on a release screen. */}
      {detail.isPending ? (
        <p role="status" data-testid="atp-loading">
          {t("production.common.loading")}
        </p>
      ) : detail.error !== null ? (
        <p className="inv-error" role="alert" data-testid="atp-error">
          {createReleaseErrorMessage(t, detail.error)}
        </p>
      ) : (
        planLines.map((line) => (
          <AtpRow
            key={`${line.component_product_id}:${line.bom_line_id ?? ""}:${line.wo_operation_id ?? ""}`}
            componentId={line.component_product_id}
            facilityId={workOrder.facility_id}
            // THE SERVER'S OWN NUMBER, as an exact decimal string — the same
            // field the Materials tab renders and the release response quotes
            // as `shortages[].required`.
            requiredQty={line.qty_planned}
            uomCode={line.uom}
            // The COMPARABLE half of the same requirement (D-221). ATP answers
            // in the component's base unit and the BOM line need not be
            // authored in it, so the number the planner reads and the number
            // the availability check uses are two different fields — both the
            // server's, neither re-derived here.
            requiredBaseQty={line.qty_planned_base}
            baseUom={line.base_uom}
            name={products.byId.get(line.component_product_id)?.name ?? ""}
            locale={i18n.language}
            onShort={markShort}
          />
        ))
      )}

      {confirming ? (
        <div
          className="production-note"
          role="alertdialog"
          aria-label={t("production.createModal.releaseAnywayTitle")}
        >
          <p>
            <strong>{t("production.createModal.releaseAnywayTitle")}</strong>
          </p>
          <p>{t("production.createModal.releaseAnywayBody", { count: shortComponents.size })}</p>
          <div className="form-actions">
            <Button
              variant="primary"
              busy={release.isPending}
              onClick={() => {
                post(true);
              }}
            >
              {t("production.createModal.releaseAnyway")}
            </Button>
            <Button
              onClick={() => {
                setConfirming(false);
              }}
            >
              {t("production.common.cancel")}
            </Button>
          </div>
        </div>
      ) : null}

      {/* A pegged work order's release meets module 09's gate (D-290): the
          order-facility 403 (`reason: order_facility_out_of_scope`),
          CREDIT_BLOCKED, SHIPMENT_OPEN and the create's peg rules again. Each
          is our own sentence (`createReleaseErrorMessage`), so the planner
          reads WHY and what lifts it; the work order stays a draft and
          Release stays here to press again once it is lifted. */}
      {failure !== undefined ? (
        <p
          className="form-error"
          role="alert"
          data-testid="wo-release-failed"
          data-code={problemCodeOf(failure)}
        >
          {createReleaseErrorMessage(t, failure)}
        </p>
      ) : null}

      {!confirming ? (
        <div className="form-actions">
          <Button
            variant="primary"
            busy={release.isPending}
            busyLabel={t("production.common.saving")}
            onClick={() => {
              post(false);
            }}
          >
            {t("production.createModal.releaseSubmit")}
          </Button>
          <Button onClick={onClose}>{t("production.createModal.releaseLater")}</Button>
        </div>
      ) : null}
    </section>
  );
}

/** One component's advisory availability row: green when covered, amber with
 * the shortfall when not. No "earliest PO due" — module 10 is absent. */
function AtpRow({
  componentId,
  facilityId,
  requiredQty,
  uomCode,
  requiredBaseQty,
  baseUom,
  name,
  locale,
  onShort,
}: {
  componentId: string;
  facilityId: string;
  /** The server's `material_lines[].qty_planned` — an exact ≤3 dp decimal
   * STRING, rendered as given. It is never re-derived or re-rounded here; the
   * only float in this component is the availability COMPARISON below, which
   * is advisory and which the server re-decides at release. */
  requiredQty: string;
  /** The BOM line's own unit — what `requiredQty` is stated in. */
  uomCode: string;
  /** `material_lines[].qty_planned_base`: the same requirement in the component
   * product's BASE unit (D-221). THE COMPARISON BELOW USES THIS ONE, because
   * `atp_qty` is a base-unit quantity and the line's own unit need only convert
   * to it: on KazKabel's copper — kg-based, drawn against a BOM in metres at
   * 0.62 kg/m — comparing `qty_planned` against `atp_qty` called every covered
   * component short by the conversion factor. Only the server can convert (the
   * factor is a per-product row in module 01), which is why this arrives as a
   * field rather than being worked out here. */
  requiredBaseQty: string;
  /** The unit `requiredBaseQty` and `atp_qty` are both in. */
  baseUom: string;
  name: string;
  locale: string;
  onShort: (componentId: string, short: boolean) => void;
}) {
  const { t } = useTranslation();
  const atp = useComponentAtp(componentId, facilityId);
  const required = Number(requiredBaseQty);
  const available = atp.data !== undefined ? Number(atp.data.atp_qty) : undefined;
  const short = available !== undefined && available < required - 1e-9;

  useEffect(() => {
    // Deps: the short FACT only — `onShort` is a parent closure recreated per
    // render and would loop this effect if keyed on.
    onShort(componentId, short);
  }, [componentId, short]);

  const requiredText = formatQty(requiredQty, { locale });
  return (
    <div
      className={`atp-row ${available === undefined ? "" : short ? "is-short" : "is-ok"}`}
      data-testid={`atp-row-${componentId}`}
    >
      <span>
        {name} · {t("production.createModal.colRequired")} {requiredText}{" "}
        {unitSymbol(uomCode, { locale })}
      </span>
      <span>
        {available === undefined
          ? t("production.createModal.atpUnknown")
          : short
            ? t("production.createModal.atpShort", {
                // In `baseUom`, not the line's unit: this is a difference
                // between two base-unit quantities, and labelling it with the
                // BOM line's unit would restate the very confusion D-221 fixed.
                qty: `${formatQty((required - available).toFixed(3), { locale })} ${unitSymbol(baseUom, { locale })}`,
              })
            : t("production.createModal.atpOk")}
      </span>
    </div>
  );
}
